/**
 * Event Schedule Configuration
 * Single source of truth for date-driven UI events.
 */

import { isChineseNewYearActive } from './chinese'
import { isDateInStaticRange } from './helpers'
import { type EventConfig, EventType } from './types'

const FORCE_EVENT_KEY = 'lotto_force_event'

const EVENT_SCHEDULE: EventConfig[] = [
  {
    type: EventType.CHRISTMAS,
    kind: 'static',
    startMonth: 12,
    startDay: 20,
    endMonth: 1,
    endDay: 10,
  },
  {
    type: EventType.AUTUMN_LEAVES,
    kind: 'static',
    startMonth: 10,
    startDay: 1,
    endMonth: 10,
    endDay: 20,
  },
  {
    type: EventType.HALLOWEEN,
    kind: 'static',
    startMonth: 10,
    startDay: 21,
    endMonth: 11,
    endDay: 16,
  },
  {
    type: EventType.SAKURA,
    kind: 'static',
    startMonth: 3,
    startDay: 18,
    endMonth: 4,
    endDay: 7,
  },
  {
    type: EventType.SONGKRAN,
    kind: 'static',
    startMonth: 4,
    startDay: 8,
    endMonth: 4,
    endDay: 16,
  },
  {
    type: EventType.CHINESE_NEW_YEAR,
    kind: 'dynamic',
    isActive: isChineseNewYearActive,
  },
  {
    type: EventType.LOY_KRATHONG,
    kind: 'static',
    startMonth: 11,
    startDay: 17,
    endMonth: 11,
    endDay: 25,
  },
]

// --- Public API ---

function getForcedEvent(): EventType | null {
  if (typeof window === 'undefined') return null

  const stored = window.sessionStorage.getItem(FORCE_EVENT_KEY)
  if (!stored) return null

  const isValidEvent = Object.values(EventType).includes(stored as EventType)
  return isValidEvent ? (stored as EventType) : null
}

export function setForcedEvent(eventType: EventType | null): void {
  if (typeof window === 'undefined') return

  if (eventType === null) {
    window.sessionStorage.removeItem(FORCE_EVENT_KEY)
  } else {
    window.sessionStorage.setItem(FORCE_EVENT_KEY, eventType)
  }
}

if (typeof window !== 'undefined') {
  // @ts-expect-error - Adding to window for debugging
  window.setForcedEvent = setForcedEvent
}

/**
 * Get the currently active event.
 * @param date - Optional date to check against (defaults to now).
 */
export function getCurrentEvent(date: Date = new Date()): EventType | null {
  const forcedEvent = getForcedEvent()
  if (forcedEvent) return forcedEvent

  for (const event of EVENT_SCHEDULE) {
    if (event.kind === 'static') {
      if (isDateInStaticRange(date, event)) {
        return event.type
      }
    } else if (event.kind === 'dynamic') {
      if (event.isActive(date)) {
        return event.type
      }
    }
  }

  return null
}

export function isEventActive(
  eventType: EventType,
  date: Date = new Date(),
): boolean {
  return getCurrentEvent(date) === eventType
}
